import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getRoles, createRole, updateRole, deleteRole, Role, RoleCreate } from '../../../services/rbac';
import { getModuleTableTheme, modernCellStyles } from '../../../theme/tableTheme';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import PermissionButton from '../../../components/PermissionButton';

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [formData, setFormData] = useState<RoleCreate>({ name: '' });
  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const data = await getRoles();
      setRoles(data);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setFormData({ name: role.name });
    } else {
      setEditingRole(null);
      setFormData({ name: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRole(null);
    setFormData({ name: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showError('Le nom du rôle est requis');
      return;
    }

    try {
      if (editingRole) {
        await updateRole(editingRole.id, formData);
        showSuccess('Rôle modifié avec succès');
      } else {
        await createRole(formData);
        showSuccess('Rôle créé avec succès');
      }
      handleCloseDialog();
      fetchRoles();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleOpenDeleteDialog = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;

    try {
      await deleteRole(roleToDelete.id);
      showSuccess('Rôle supprimé avec succès');
      handleCloseDeleteDialog();
      fetchRoles();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Gestion des Rôles
          </Typography>
          <PermissionButton
            moduleName="Admin"
            operation="add"
            miniModuleName="Roles"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Rôle
          </PermissionButton>
        </Box>

        <TableContainer sx={getModuleTableTheme('administration')}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={modernCellStyles}>Nom</TableCell>
                <TableCell sx={modernCellStyles}>Date de création</TableCell>
                <TableCell sx={modernCellStyles} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">Chargement...</TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">Aucun rôle trouvé</TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell>
                      <Chip label={role.name} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      {new Date(role.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell align="right">
                      <PermissionButton
                        moduleName="Admin"
                        operation="edit"
                        miniModuleName="Roles"
                        component="IconButton"
                        onClick={() => handleOpenDialog(role)}
                        size="small"
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Admin"
                        operation="delete"
                        miniModuleName="Roles"
                        component="IconButton"
                        onClick={() => handleOpenDeleteDialog(role)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du rôle"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRole ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le rôle "{roleToDelete?.name}" ?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            Attention: Tous les profils associés à ce rôle seront également supprimés.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
